import React from 'react';
import PropTypes from 'prop-types';
import { sortBy } from 'lodash';
import { createSelector } from 'reselect';

import ProjectCountPortal from './project_count_portal';
import LeadsList from 'components/project/leads_list';
import ContactRolesList from 'components/project/contact_roles_list';
import ContactAddProjectLeadButton from 'bundles/contact/projects/contact_add_project_lead_button';
import ContactAddProjectContactRoleButton from 'bundles/contact/projects/contact_add_project_contact_role_button';

const getContactRoles = createSelector(
  [props => props.contactRoles],
  contactRoles => sortBy(Object.values(contactRoles), contactRole => contactRole.parentName.toLowerCase())
);

const getLeads = createSelector(
  [props => props.leads],
  leads => sortBy(Object.values(leads), lead => lead.projectName.toLowerCase())
);

export default class ContactProjectSection extends React.Component {
  static propTypes = {
    contact: PropTypes.object.isRequired,
    contactRoles: PropTypes.object.isRequired,
    leads: PropTypes.object.isRequired,
    leadStatuses: PropTypes.array.isRequired,
    referralSources: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })),
    relationships: PropTypes.array.isRequired,
    showLeadsSection: PropTypes.bool.isRequired
  };

  state = {
    contactRoles: this.props.contactRoles,
    leads: this.props.leads
  }

  hasContactRoles = () => getContactRoles(this.state).length > 0

  handleContactRoleChange = (newRole) => {
    this.setState((prevState) => {
      return { contactRoles: { ...prevState.contactRoles, [newRole.id]: newRole } };
    });
  }

  hasLeads = () => getLeads(this.state).length > 0

  handleLeadChange = (newLead) => {
    this.setState((prevState) => {
      return { leads: { ...prevState.leads, [newLead.id]: newLead } };
    });
  }

  handleDeleteContactRole = (role) => {
    this.setState(({ contactRoles }) => {
      const { [role.id]: _,  ...remainingRoles } = contactRoles;

      return { contactRoles: remainingRoles };
    });
  };

  renderLeadsSection() {
    if (!this.props.showLeadsSection) return;

    return (
      <React.Fragment>
        {this.renderLeadsHeader()}
        {this.hasLeads() ? this.renderLeadsList() : this.renderEmptyState()}
      </React.Fragment>);
  }

  render() {
    return (
      <div className="project-leads">
        {this.renderLeadsSection()}
        {this.renderContactRolesHeader()}
        {this.hasContactRoles() ? this.renderContactRolesList() : this.renderEmptyState()}
        <ProjectCountPortal count={[...getContactRoles(this.state), ...getLeads(this.state)].length} />
      </div>
    );
  }

  renderContactRolesHeader() {
    const { contact, relationships } = this.props;

    return (
      <React.Fragment>
        <h3 className="d-inline-block">
          <span className="ml-1">Contact</span>
        </h3>

        <ContactAddProjectContactRoleButton
          className="btn btn-create btn-sm pull-right"
          contact={contact}
          relationships={relationships}
          onAdd={this.handleContactRoleChange}
        />
      </React.Fragment>
    );
  }

  renderLeadsHeader() {
    const { contact, leadStatuses, referralSources } = this.props;

    return (
      <React.Fragment>
        <h3 className="d-inline-block">
          <span className="ml-1">Leads</span>
        </h3>

        <ContactAddProjectLeadButton
          className="btn btn-create btn-sm pull-right"
          contact={contact}
          leadStatuses={leadStatuses}
          referralSources={referralSources}
          onAdd={this.handleLeadChange}
        />
      </React.Fragment>
    );
  }

  renderEmptyState() {
    return (
      <div className="panel panel-default dashed-border">
        <div className="panel-body">
          <h4 className="text-center text-muted">
            No projects are associated with this contact.
          </h4>
        </div>
      </div>
    );
  }

  renderContactRolesList() {
    return <ContactRolesList contactRoles={getContactRoles(this.state)} onDelete={this.handleDeleteContactRole} />;
  }

  renderLeadsList() {
    return <LeadsList leads={getLeads(this.state)} />;
  }
}
